import type { Transition, Variants } from "framer-motion";

/**
 * Animation budget (exactly 6):
 * 1. Primary CTA glow/lift — CSS `.btn-primary`
 * 2. Number changes — AnimatedNumber spring
 * 3. Card/list entrance — listStagger / listItemFade
 * 4. CR gauge stroke — RatioGauge CSS transition
 * 5. Skeleton shimmer — CSS `.skeleton-shimmer`
 * 6. Modal/Drawer — drawerVariants / modalVariants
 */

export const instantTransition: Transition = { duration: 0 };

export const springPop: Transition = {
	type: "spring",
	stiffness: 400,
	damping: 28,
};

export function motionTransition(
	reducedMotion: boolean | null,
	fallback: Transition = springPop,
): Transition {
	if (reducedMotion) return instantTransition;
	return fallback;
}

/** Animation #3 */
export const listStagger: Variants = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: { staggerChildren: 0.06, delayChildren: 0.04 },
	},
};

export const listItemFade: Variants = {
	hidden: { opacity: 0, y: 12 },
	show: { opacity: 1, y: 0, transition: springPop },
	exit: { opacity: 0, transition: { duration: 0.12 } },
};

export const pageStagger = listStagger;
export const sectionFadeUp = listItemFade;

/** Animation #6 */
export const drawerVariants: Variants = {
	hidden: { opacity: 0, y: "100%" },
	show: {
		opacity: 1,
		y: 0,
		transition: { type: "spring", stiffness: 380, damping: 32 },
	},
	exit: { opacity: 0, y: "40%", transition: { duration: 0.18 } },
};

export const modalVariants: Variants = {
	hidden: { opacity: 0, scale: 0.96 },
	show: {
		opacity: 1,
		scale: 1,
		transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
	},
	exit: { opacity: 0, scale: 0.98, transition: { duration: 0.15 } },
};

export const overlayVariants: Variants = {
	hidden: { opacity: 0 },
	show: { opacity: 1, transition: { duration: 0.15 } },
	exit: { opacity: 0, transition: { duration: 0.12 } },
};

export function withReducedMotion(
	variants: Variants,
	reducedMotion: boolean | null,
): Variants {
	if (reducedMotion) {
		return {
			hidden: { opacity: 1 },
			show: { opacity: 1 },
			exit: { opacity: 1 },
		};
	}
	return variants;
}

/** Compat aliases — budget: map extras to allowed #3 fade (no extra motion language) */
export const viewportReveal = listItemFade;
export const cinematicReveal = listItemFade;
export const tabContentFade = listItemFade;
export const cardHoverLift = undefined;
